import { useMemo } from 'react';
import type { ApplicationId, ObjectId, SpatialObjectKind } from '@/types';
import type { ApplicationApi, ApplicationRegistry } from '@/applications';
import type { SceneGraph } from '@/core/scene/sceneGraph';
import { useWorkspaceStore, selectActiveDocument } from '@/store/workspaceStore';
import { useSelectionStore } from '@/store/selectionStore';
import { centerOnObject } from '@/features/camera/cameraNavigation';
import { openApplication } from './windowManager';

/**
 * The shell-provided API handed to applications. Reads go through getters so the
 * object is stable across renders; applications that need to re-render on workspace
 * changes declare `observesWorkspace` and the window chrome subscribes for them.
 */
export const useApplicationApi = (scene: SceneGraph, applications: ApplicationRegistry): ApplicationApi =>
  useMemo<ApplicationApi>(() => {
    const document = () => {
      const d = selectActiveDocument(useWorkspaceStore.getState());
      if (!d) throw new Error('No active workspace');
      return d;
    };
    return {
      get document() {
        return document();
      },
      get selectedIds() {
        return useSelectionStore.getState().selection.objectIds;
      },
      get historyDepth() {
        const s = useWorkspaceStore.getState();
        return s.histories[s.activeWorkspaceId]?.past.length ?? 0;
      },
      applications: applications.list(),
      listObjects: (kind?: SpatialObjectKind) => {
        const all = Object.values(document().objects);
        return kind ? all.filter((o) => o.kind === kind) : all;
      },
      selectObjects: (ids: readonly ObjectId[]) => {
        useSelectionStore.getState().select(ids);
      },
      centerOn: (id: ObjectId) => {
        centerOnObject(scene, id);
      },
      openApplication: (id: ApplicationId) => {
        openApplication(id, applications);
      },
    };
  }, [scene, applications]);
